import React from "react";
import { useQuery } from "@tanstack/react-query";
import { CrudSection } from "components/admin/workspace-crud";
import {
  buildPlayerClassCreateValues,
  buildPlayerClassFields,
  buildPlayerClassUpdateValues,
} from "pages/app/workspace/sections/forms/athlete-section-forms";
import { workspaceApi } from "pages/app/workspace/services";
import { formatDateTime, formatMoney } from "utils/admin";
import { canManageOwnedAthleteClasses, hasLinkedPlayerProfile } from "utils/portal-access";

export const PlayerClassesSection: React.FC<{ currentUser: any; portalAccess: any }> = ({
  currentUser,
  portalAccess,
}) => {
  const { data: athletes = [] } = useQuery({
    queryKey: ["workspace-classes-athletes"],
    queryFn: () => workspaceApi.athletes.list(),
  });
  const classesQuery = useQuery({
    queryKey: ["workspace-player-classes", currentUser?.player?.id || "all"],
    queryFn: () =>
      workspaceApi.playerClasses.list({
        page: 1,
        per_page: 50,
        player_id:
          hasLinkedPlayerProfile(currentUser) && currentUser?.role !== "admin"
            ? currentUser.player.id
            : undefined,
      }),
  });
  const defaultPlayerId = currentUser?.player?.id || athletes[0]?.id || "";
  const canManageClasses = canManageOwnedAthleteClasses(currentUser, portalAccess);
  const visibleAthletes =
    hasLinkedPlayerProfile(currentUser) && currentUser?.role !== "admin"
      ? athletes.filter((athlete: any) => athlete.id === currentUser?.player?.id)
      : athletes;
  const classFields = buildPlayerClassFields(visibleAthletes);

  return (
    <CrudSection
      buildCreateValues={() => buildPlayerClassCreateValues(defaultPlayerId)}
      buildUpdateValues={(row) => buildPlayerClassUpdateValues(currentUser, row)}
      canCreate={canManageClasses && Boolean(defaultPlayerId)}
      canEdit={canManageClasses}
      canRemove={canManageClasses}
      columns={[
        { label: "Class", accessor: (row) => row.title || "Untitled class" },
        {
          label: "Athlete",
          accessor: (row) => row.player_name || row.player?.full_name || "Unknown",
        },
        { label: "Price", accessor: (row) => row.formatted_price || formatMoney(row.price) },
        { label: "Created", accessor: (row) => formatDateTime(row.created_at) },
      ]}
      createLabel="Add class"
      data={classesQuery.data || []}
      description="Manage player class catalog entries and keep the storefront training offer aligned with the latest API contract."
      emptyDescription="No player classes have been created yet."
      fields={classFields}
      invalidateKeys={[["workspace-player-classes"]]}
      isLoading={classesQuery.isLoading}
      onCreate={(payload) => workspaceApi.playerClasses.create(payload)}
      onRemove={(row) => workspaceApi.playerClasses.remove(row.id)}
      onUpdate={(row, payload) => workspaceApi.playerClasses.update(row.id, payload)}
      title="Player Classes"
    />
  );
};
